/**
 * TeamLeader Exam Management Workspace — Dashboard page.
 *
 * Analytics summary cards + a filterable, paginated grid of exams scoped to the caller's
 * managed groups/students (server-side via api/teamleader/exams/*). Rows link to the (not yet
 * built) per-exam detail page at /team-leader/exams/$examId.
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useTlExamsDashboard,
  useTlAnalyticsSummary,
  useTlAtRiskStudents,
} from "@/hooks/use-tl-exams";
import type { TlAtRiskStudent, TlExamListItem, TlExamListParams } from "@/lib/api/tl-exams";
import { useMyGroupContent, useMyGroups } from "@/hooks/use-teamleader";
import { fetchStudentGrid } from "@/lib/api/students";
import { usePaginationState } from "@/hooks/use-pagination-state";
import { PaginationControls } from "@/components/PaginationControls";
import { TableSkeleton, CardSkeleton } from "@/components/loading";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowDownCircle,
  ArrowUp,
  ArrowUpCircle,
  ArrowUpDown,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileText,
  FilterX,
  History,
  Repeat,
  RotateCcw,
  Search,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { AllAttemptsTab } from "@/components/team-leader/exams/AllAttemptsTab";

export const Route = createFileRoute("/_authenticated/team-leader/exams")({
  component: TeamLeaderExamsDashboard,
});

// ── Static option sets ───────────────────────────────────────────────────────
// Mirrors EXAM_CATEGORY_OPTIONS in admin/exam-center.tsx — same backend ExamType enum values.
const EXAM_TYPE_OPTIONS = [
  { value: "PracticeQuiz", label: "Practice Quiz" },
  { value: "CheckpointExam", label: "Checkpoint" },
  { value: "CourseFinal", label: "Course Final" },
  { value: "TrackFinal", label: "Track Final" },
  { value: "Assignment", label: "Assignment" },
  { value: "Survey", label: "Survey" },
] as const;

const EXAM_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  EXAM_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

// Backend-computed exam status facet (TlExamListItemDto.Status): derived from
// Exam.IsActive/StartDate/EndDate — "Active" | "Closed" | "Upcoming".
//
// These three are the ONLY values the server acts on: TeamLeaderExamService.ApplyStatusFilter
// switches on "active"/"closed"/"upcoming" (case-insensitively) and its default arm returns the
// query unfiltered. So any value added here that the switch does not name would silently do
// nothing — a filter that looks applied and is not. Attempt status (InProgress / PendingGrading /
// Completed …) is a different axis on ExamSession and is NOT selectable here; the per-exam roster
// owns that, and "what can I actually grade" comes from the grader-scoped queue, never from a
// status string.
const STATUS_OPTIONS = ["Active", "Closed", "Upcoming"] as const;

function statusBadgeClass(status: string): string {
  switch (status) {
    case "Active":
      return "bg-success/15 text-success border-success/30";
    case "Upcoming":
      return "bg-warning/15 text-warning border-warning/30";
    case "Closed":
      return "bg-muted text-muted-foreground border-border";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

// ── Formatting helpers ───────────────────────────────────────────────────────
function formatScore(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v)}%`;
}

function formatCount(v: number | null | undefined): string {
  return v === null || v === undefined ? "0" : String(v);
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Debounce a rapidly-changing value (search input) so each keystroke doesn't trigger a
// server round-trip — mirrors useDebouncedValue in admin/exam-center.tsx (no shared hook exists).
function useDebouncedValue<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/**
 * The columns `GetExamsDashboardAsync` can actually order by (`ApplySort`'s whitelist). Anything not
 * in this set is deliberately NOT clickable: the server falls back to title for an unknown key, so a
 * sortable-looking header would silently sort by something else.
 *
 * Note the cost, which is why `title` stays the default: every non-title key is a rolled-up metric
 * that does not exist on the Exam row, so the server materialises every exam in scope and sorts in
 * memory before paging.
 */
const SORTABLE_COLUMNS = {
  title: "title",
  assignedCount: "assignedcount",
  completedCount: "completedcount",
  abandonedCount: "abandonedcount",
  averageScore: "averagescore",
  lastAttemptAt: "lastattempt",
} as const;

type SortableColumn = keyof typeof SORTABLE_COLUMNS;

/** A header that toggles asc/desc on its own column and hands the sort to the server. */
function SortableHead({
  column,
  label,
  align = "left",
  sortBy,
  sortDir,
  onSort,
}: {
  column: SortableColumn;
  label: string;
  align?: "left" | "right";
  sortBy?: string;
  sortDir?: "asc" | "desc";
  onSort: (by: string, dir: "asc" | "desc") => void;
}) {
  const key = SORTABLE_COLUMNS[column];
  const active = sortBy === key;
  // First click on a metric shows the biggest numbers first — "which exams have the most abandoned
  // attempts" is the question being asked, and ascending would answer it with a screen of zeroes.
  const nextDir: "asc" | "desc" = active && sortDir === "desc" ? "asc" : "desc";

  return (
    <TableHead className={align === "right" ? "text-right" : undefined}>
      <button
        type="button"
        onClick={() => onSort(key, nextDir)}
        aria-label={`Sort by ${label} ${nextDir === "desc" ? "descending" : "ascending"}`}
        className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
          align === "right" ? "flex-row-reverse" : ""
        } ${active ? "text-foreground font-semibold" : ""}`}
      >
        {label}
        {active ? (
          sortDir === "desc" ? (
            <ArrowDown className="h-3.5 w-3.5" />
          ) : (
            <ArrowUp className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

// ── Analytics cards ───────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}) {
  return (
    <Card className="overflow-hidden border border-t-[3px]" style={{ borderTopColor: color }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {label}
          </span>
          <div
            className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: color }}
          >
            <Icon className="h-3.5 w-3.5 text-white" />
          </div>
        </div>
        <p className="text-xl font-bold tracking-tight">{value}</p>
      </CardContent>
    </Card>
  );
}

function AnalyticsCardsRow({ filters }: { filters: TlExamListParams }) {
  const { data, isLoading, isError, refetch } = useTlAnalyticsSummary(filters);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {Array.from({ length: 13 }).map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-4">
              <Skeleton className="h-3 w-20 mb-3" />
              <Skeleton className="h-6 w-14" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Failed to load analytics.</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RotateCcw className="h-4 w-4 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const cards = [
    {
      label: "Total Students",
      value: formatCount(data.totalStudents),
      icon: Users,
      color: "var(--chart-1)",
    },
    {
      label: "Active Attempts",
      value: formatCount(data.activeAttempts),
      icon: Activity,
      color: "var(--chart-2)",
    },
    {
      label: "Completed Attempts",
      value: formatCount(data.completedAttempts),
      icon: CheckCircle2,
      color: "var(--success)",
    },
    {
      label: "Passed",
      value: formatCount(data.passed),
      icon: CheckCircle2,
      color: "var(--success)",
    },
    {
      label: "Failed",
      value: formatCount(data.failed),
      icon: XCircle,
      color: "var(--destructive)",
    },
    {
      label: "Pending Grading",
      value: formatCount(data.pendingGrading),
      icon: ClipboardList,
      color: "var(--warning)",
    },
    {
      label: "Average Score",
      value: formatScore(data.averageScore),
      icon: TrendingUp,
      color: "var(--chart-3)",
    },
    {
      label: "Highest Score",
      value: formatScore(data.highestScore),
      icon: ArrowUpCircle,
      color: "var(--success)",
    },
    {
      label: "Lowest Score",
      value: formatScore(data.lowestScore),
      icon: ArrowDownCircle,
      color: "var(--destructive)",
    },
    {
      label: "Average Duration",
      value: formatDuration(data.averageDurationSeconds),
      icon: Clock,
      color: "var(--chart-4)",
    },
    {
      label: "Violation Rate",
      value: `${Math.round(data.violationRate ?? 0)}%`,
      icon: AlertTriangle,
      color: "var(--warning)",
    },
    {
      label: "Essay Pending",
      value: formatCount(data.essayPending),
      icon: FileText,
      color: "var(--chart-2)",
    },
    {
      label: "Retake Count",
      value: formatCount(data.retakeCount),
      icon: Repeat,
      color: "var(--chart-5)",
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
      {cards.map((c) => (
        <StatCard key={c.label} label={c.label} value={c.value} icon={c.icon} color={c.color} />
      ))}
    </div>
  );
}

// ── Students At Risk widget ───────────────────────────────────────────────────
// Compact top-5 slice; "View all" deep-links to the full paginated page at
// /team-leader/exams/at-risk. Respects the same course/group filter context as the rest of the
// dashboard when a specific course/group is selected.
const RISK_FLAG_BADGES: Array<{
  key: keyof TlAtRiskStudent;
  label: string;
}> = [
  { key: "failingGradesFlag", label: "Failing Grades" },
  { key: "lowActivityFlag", label: "Low Activity" },
  { key: "checkpointRiskFlag", label: "Checkpoint Risk" },
  { key: "violationRiskFlag", label: "Violations" },
];

function AtRiskStudentRow({ student, onClick }: { student: TlAtRiskStudent; onClick: () => void }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors border-b last:border-b-0"
      onClick={onClick}
    >
      <div className="min-w-[160px]">
        <p className="text-sm font-medium leading-tight">{student.studentName}</p>
        <p className="text-xs text-muted-foreground leading-tight">{student.email}</p>
        {student.groupName && (
          <p className="text-xs text-muted-foreground/80 leading-tight">{student.groupName}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 flex-1">
        {RISK_FLAG_BADGES.filter((f) => Boolean(student[f.key])).map((f) => (
          <Badge
            key={f.key}
            variant="outline"
            className="text-[10px] bg-destructive/10 text-destructive border-destructive/30"
          >
            {f.label}
          </Badge>
        ))}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <ShieldAlert className="h-3.5 w-3.5 text-destructive" />
        <span className="text-sm font-semibold tabular-nums">{Math.round(student.riskScore)}</span>
        <span className="text-xs text-muted-foreground">risk score</span>
      </div>

      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </div>
  );
}

function StudentsAtRiskWidget({ courseId, groupId }: { courseId?: string; groupId?: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useTlAtRiskStudents({
    courseId,
    groupId,
    minRiskFlags: 1,
    pageNumber: 1,
    pageSize: 5,
  });

  const students: TlAtRiskStudent[] = data?.items ?? [];

  // The canonical student destination is the profile page's Exams panel. The older
  // `/team-leader/exams/student/$studentId` still resolves for existing bookmarks, but nothing
  // in-app points at it any more: it duplicates this page's exam history from the same endpoint and
  // carries a second, weaker copy of the moderation rules.
  const goToStudent = (studentId: string) => {
    navigate({
      to: "/team-leader/students/$studentId",
      params: { studentId },
      search: { tab: "exams" as const },
    });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-destructive" />
              Students At Risk
            </CardTitle>
            <CardDescription>
              {data
                ? `${data.totalCount} student${data.totalCount === 1 ? "" : "s"} flagged`
                : "Failing grades, low activity, checkpoint or violation risk"}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild className="gap-1 shrink-0">
            <Link
              to="/team-leader/exams/at-risk"
              search={{ courseId, groupId }}
              aria-label="View all students at risk"
            >
              View all
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4">
            <CardSkeleton count={3} />
          </div>
        ) : isError ? (
          <div className="p-10 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Failed to load at-risk students.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RotateCcw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </div>
        ) : students.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-success/40" />
            No students currently at risk.
          </div>
        ) : (
          <div>
            {students.map((s) => (
              <AtRiskStudentRow
                key={s.studentId}
                student={s}
                onClick={() => goToStudent(s.studentId)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
function TeamLeaderExamsDashboard() {
  const navigate = useNavigate();
  const pagination = usePaginationState({ initialPageSize: 25 });
  const [viewMode, setViewMode] = useState<"exams" | "attempts">("exams");

  // Raw (immediate) search input, debounced before it drives the query / pagination state.
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  useEffect(() => {
    pagination.setSearch(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const [courseFilter, setCourseFilter] = useState("all");
  const [trackFilter, setTrackFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [examTypeFilter, setExamTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [studentId, setStudentId] = useState<string | null>(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  const setAndReset =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      pagination.reset();
    };

  // Filter options come from what the caller's groups actually deliver, not from the org-wide
  // assignable catalogue. Every exam this page can list is already scoped to those groups, so a
  // catalogue-wide dropdown offered mostly options that narrow the table to nothing.
  const { courses, tracks } = useMyGroupContent();
  const { data: groups = [] } = useMyGroups();

  const debouncedStudentSearch = useDebouncedValue(studentSearchTerm, 300);
  const { data: studentSearchData, isLoading: studentSearchLoading } = useQuery({
    queryKey: ["tl", "exams", "student-search", debouncedStudentSearch],
    queryFn: () => fetchStudentGrid({ search: debouncedStudentSearch, pageSize: 20 }),
  });

  const studentOptions: ComboboxOption[] = useMemo(
    () =>
      (studentSearchData?.items ?? []).map((s) => ({
        value: s.id,
        label: s.fullName || s.email,
        hint: s.email,
      })),
    [studentSearchData],
  );

  const hasActiveFilters =
    courseFilter !== "all" ||
    trackFilter !== "all" ||
    groupFilter !== "all" ||
    examTypeFilter !== "all" ||
    statusFilter !== "all" ||
    !!dateFrom ||
    !!dateTo ||
    !!studentId ||
    !!searchInput;

  const clearFilters = () => {
    setSearchInput("");
    setCourseFilter("all");
    setTrackFilter("all");
    setGroupFilter("all");
    setExamTypeFilter("all");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setStudentId(null);
    pagination.reset();
  };

  // Shared filter facets (used by both the analytics summary and the grid — the summary
  // endpoint ignores the paging fields when present, so it's safe to build the grid params
  // by spreading this object plus paging).
  const filters: TlExamListParams = useMemo(
    () => ({
      search: pagination.search || undefined,
      courseId: courseFilter !== "all" ? courseFilter : undefined,
      trackId: trackFilter !== "all" ? trackFilter : undefined,
      groupId: groupFilter !== "all" ? groupFilter : undefined,
      studentId: studentId ?? undefined,
      examType: examTypeFilter !== "all" ? examTypeFilter : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [
      pagination.search,
      courseFilter,
      trackFilter,
      groupFilter,
      studentId,
      examTypeFilter,
      statusFilter,
      dateFrom,
      dateTo,
    ],
  );

  const listParams: TlExamListParams = useMemo(
    () => ({
      ...filters,
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      // Server-side: the grid is paged, so ordering a fetched page would only sort 100 of 162 rows.
      sortBy: pagination.sortBy,
      sortDir: pagination.sortDir,
    }),
    [filters, pagination.pageNumber, pagination.pageSize, pagination.sortBy, pagination.sortDir],
  );

  const { data, isLoading, isError, refetch, isFetching } = useTlExamsDashboard(listParams);
  const exams: TlExamListItem[] = data?.items ?? [];

  const goToExam = (examId: string) => {
    navigate({ to: "/team-leader/exams/$examId", params: { examId } });
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary shadow-inner">
              <ClipboardCheck className="h-6 w-6" />
            </div>
            Exam Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor exam performance and progress across your managed groups
          </p>
        </div>
      </div>

      {/* ── View Mode Switcher Tabs ── */}
      <div className="flex items-center gap-2 border-b pb-3">
        <Button
          type="button"
          variant={viewMode === "exams" ? "default" : "outline"}
          size="sm"
          className="gap-2 h-9 text-xs"
          onClick={() => setViewMode("exams")}
        >
          <ClipboardCheck className="h-4 w-4" />
          Exams Directory
        </Button>
        <Button
          type="button"
          variant={viewMode === "attempts" ? "default" : "outline"}
          size="sm"
          className="gap-2 h-9 text-xs"
          onClick={() => setViewMode("attempts")}
        >
          <History className="h-4 w-4" />
          All Student Attempts &amp; Quick Actions
        </Button>
      </div>

      {viewMode === "attempts" ? (
        <AllAttemptsTab
          groups={groups}
          courses={courses}
        />
      ) : (
        <>
          {/* ── Analytics cards ── */}
          <AnalyticsCardsRow filters={filters} />

          {/* ── Students At Risk ── */}
          <StudentsAtRiskWidget courseId={filters.courseId} groupId={filters.groupId} />

          {/* ── Filters ── */}
          <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search exams by name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={courseFilter} onValueChange={setAndReset(setCourseFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All courses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All courses</SelectItem>
                {courses.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={trackFilter} onValueChange={setAndReset(setTrackFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All tracks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tracks</SelectItem>
                {tracks.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={groupFilter} onValueChange={setAndReset(setGroupFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={examTypeFilter} onValueChange={setAndReset(setExamTypeFilter)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {EXAM_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setAndReset(setStatusFilter)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Combobox
              options={studentOptions}
              value={studentId}
              onChange={setAndReset(setStudentId)}
              onSearchChange={setStudentSearchTerm}
              isLoading={studentSearchLoading}
              placeholder="All students"
              searchPlaceholder="Search students..."
              emptyMessage="No students found."
              className="w-48"
            />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
                <FilterX className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-xs font-medium text-muted-foreground">Attempt date range:</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setAndReset(setDateFrom)(e.target.value)}
              className="w-40"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setAndReset(setDateTo)(e.target.value)}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-primary" />
                Exams
              </CardTitle>
              <CardDescription>
                {data ? `${data.totalCount} exam${data.totalCount === 1 ? "" : "s"}` : "Loading…"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={8} />
            </div>
          ) : isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Failed to load exams.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : exams.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
              No exams match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* Only the whitelisted columns are clickable — see SORTABLE_COLUMNS. */}
                    <SortableHead
                      column="title"
                      label="Exam Name"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <TableHead>Course</TableHead>
                    <TableHead>Track</TableHead>
                    <TableHead>Type</TableHead>
                    <SortableHead
                      column="assignedCount"
                      label="Assigned"
                      align="right"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <SortableHead
                      column="completedCount"
                      label="Completed"
                      align="right"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <TableHead className="text-right">Passed</TableHead>
                    <TableHead className="text-right">Failed</TableHead>
                    <TableHead className="text-right">Pending Grading</TableHead>
                    <SortableHead
                      column="abandonedCount"
                      label="Abandoned"
                      align="right"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <SortableHead
                      column="averageScore"
                      label="Avg Score"
                      align="right"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <TableHead className="text-right">Highest</TableHead>
                    <TableHead className="text-right">Lowest</TableHead>
                    <TableHead className="text-right">Avg Duration</TableHead>
                    <TableHead className="text-right">Active Sessions</TableHead>
                    <SortableHead
                      column="lastAttemptAt"
                      label="Last Attempt"
                      sortBy={pagination.sortBy}
                      sortDir={pagination.sortDir}
                      onSort={pagination.setSort}
                    />
                    <TableHead>Status</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exams.map((exam) => (
                    <TableRow
                      key={exam.examId}
                      className="cursor-pointer"
                      onClick={() => goToExam(exam.examId)}
                    >
                      <TableCell className="font-medium">{exam.title}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {exam.courseName || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {exam.trackName || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {EXAM_TYPE_LABELS[exam.examType] ?? exam.examType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCount(exam.assignedCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCount(exam.completedCount)}
                      </TableCell>
                      <TableCell className="text-right text-success">
                        {formatCount(exam.passedCount)}
                      </TableCell>
                      <TableCell className="text-right text-destructive">
                        {formatCount(exam.failedCount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCount(exam.pendingGradingCount)}
                      </TableCell>
                      {/* Attempts the expiry sweep closed with no answers. An em dash when the API
                          build predates the field — "unknown" is not the same claim as "none". */}
                      <TableCell
                        className={`text-right ${exam.abandonedCount ? "text-destructive font-medium" : ""}`}
                      >
                        {exam.abandonedCount === undefined ? "—" : formatCount(exam.abandonedCount)}
                      </TableCell>
                      <TableCell className="text-right">{formatScore(exam.averageScore)}</TableCell>
                      <TableCell className="text-right">{formatScore(exam.highestScore)}</TableCell>
                      <TableCell className="text-right">{formatScore(exam.lowestScore)}</TableCell>
                      <TableCell className="text-right">
                        {formatDuration(exam.averageDurationSeconds)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCount(exam.activeSessionsCount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(exam.lastAttemptAt)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${statusBadgeClass(exam.status)}`}
                        >
                          {exam.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {data && (
            <div className="px-2">
              <PaginationControls
                pageNumber={data.pageNumber}
                pageSize={data.pageSize}
                totalPages={data.totalPages}
                totalCount={data.totalCount}
                hasNextPage={data.hasNextPage}
                hasPreviousPage={data.hasPreviousPage}
                onPageChange={pagination.setPageNumber}
                onPageSizeChange={pagination.setPageSize}
                isLoading={isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </div>
  );
}
